/**
 * Typed API helpers for all screens.
 * Wraps the base api client with typed return shapes.
 */

import { api } from './api.js';
import type {
  ApiLead,
  ApiTeamMember,
  ApiTcpaRule,
  ApiWorkspace,
  PaginatedResponse,
} from '@contactrate/shared';

// ── Lead queries ────────────────────────────────────────────────────────────

export interface LeadListQuery {
  stage?: string;
  source?: string;
  search?: string;
  tag?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchLeads(q: LeadListQuery = {}): Promise<PaginatedResponse<ApiLead>> {
  const params = new URLSearchParams();
  if (q.stage) params.set('stage', q.stage);
  if (q.source) params.set('source', q.source);
  if (q.search) params.set('search', q.search);
  if (q.tag) params.set('tag', q.tag);
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));

  const qs = params.toString();
  const { data, ok, status } = await api.get<PaginatedResponse<ApiLead>>(
    `/api/leads${qs ? `?${qs}` : ''}`,
  );
  if (!ok || !data) throw new Error(`fetchLeads failed (${status})`);
  return data;
}

export interface ApiLeadWithThread extends ApiLead {
  threadEvents: import('@contactrate/shared').ApiThreadEvent[];
}

export async function fetchLead(id: string): Promise<ApiLeadWithThread> {
  const { data, ok, status } = await api.get<ApiLeadWithThread>(`/api/leads/${id}`);
  if (!ok || !data) throw new Error(`fetchLead(${id}) failed (${status})`);
  return data;
}

export async function patchLead(id: string, body: Record<string, unknown>): Promise<ApiLead> {
  const { data, ok, status } = await api.patch<ApiLead>(`/api/leads/${id}`, body);
  if (!ok || !data) throw new Error(`patchLead(${id}) failed (${status})`);
  return data;
}

// ── Settings ────────────────────────────────────────────────────────────────

export interface WorkspaceSettings {
  must_know?: string[];
  nice_to_know?: string[];
  hard_dq?: string[];
  notifications?: Record<string, boolean>;
}

export async function fetchSettings(): Promise<ApiWorkspace & { settings?: WorkspaceSettings }> {
  const { data, ok, status } = await api.get<ApiWorkspace>('/api/settings');
  if (!ok || !data) throw new Error(`fetchSettings failed (${status})`);
  return data as ApiWorkspace & { settings?: WorkspaceSettings };
}

export async function patchSettings(body: Record<string, unknown>): Promise<ApiWorkspace> {
  const { data, ok, status } = await api.patch<ApiWorkspace>('/api/settings', body);
  if (!ok || !data) throw new Error(`patchSettings failed (${status})`);
  return data;
}

// ── Team ─────────────────────────────────────────────────────────────────────

export async function fetchTeam(): Promise<ApiTeamMember[]> {
  const { data, ok, status } = await api.get<ApiTeamMember[]>('/api/team');
  if (!ok || !data) throw new Error(`fetchTeam failed (${status})`);
  return data;
}

export async function patchTeamMember(id: string, body: Partial<ApiTeamMember>): Promise<ApiTeamMember> {
  const { data, ok, status } = await api.patch<ApiTeamMember>(`/api/team/${id}`, body);
  if (!ok || !data) throw new Error(`patchTeamMember(${id}) failed (${status})`);
  return data;
}

// ── TCPA ─────────────────────────────────────────────────────────────────────

export async function fetchTcpa(): Promise<ApiTcpaRule[]> {
  const { data, ok, status } = await api.get<ApiTcpaRule[]>('/api/tcpa');
  if (!ok || !data) throw new Error(`fetchTcpa failed (${status})`);
  return data;
}

export async function overrideTcpa(id: string, enforced: boolean, reason: string): Promise<ApiTcpaRule> {
  const { data, ok, status } = await api.patch<ApiTcpaRule>(`/api/tcpa/${id}/override`, { enforced, reason });
  if (!ok || !data) throw new Error(`overrideTcpa(${id}) failed (${status})`);
  return data;
}

// ── Read models (new endpoints) ──────────────────────────────────────────────

export interface DashboardStats {
  range: string;
  newLeads: number;
  contacts: number;
  contactRate: number;
  avgTouchesToContact: number | null;
  bookRate: number;
  liveTransfers: number;
  dials: number;
  answered: number;
  answerRate: number;
  avgCallLengthS: number;
  avgCallsToContact: number;
  smsSent: number;
  smsReplies: number;
  replyRate: number;
  firstReplyMedianS: number;
  optOuts: number;
}

export async function fetchDashboardStats(range: string): Promise<DashboardStats> {
  const { data, ok, status } = await api.get<DashboardStats>(`/api/dashboard/stats?range=${range}`);
  if (!ok || !data) throw new Error(`fetchDashboardStats failed (${status})`);
  return data;
}

export interface LiveState {
  liveCalls: LiveCall[];
  queueCount: number;
  nextDialIn: string | null;
  quietHoursEnforced: boolean;
  queuePaused: boolean;
}

export interface LiveCall {
  leadId: string;
  leadName: string;
  initials: string;
  state: string;
  attemptNo: number;
  phase: string;
  sentiment: 'positive' | 'neutral' | 'tense';
  durationS: number;
  transcript: Array<{ who: string; text: string }>;
  transferReady: boolean;
}

export async function fetchLiveState(): Promise<LiveState> {
  const { data, ok, status } = await api.get<LiveState>('/api/live/state');
  if (!ok || !data) throw new Error(`fetchLiveState failed (${status})`);
  return data;
}

export interface ActivityItem {
  id: string;
  text: string;
  ts: string;
  kind: string;
}

export async function fetchActivity(): Promise<ActivityItem[]> {
  const { data, ok, status } = await api.get<ActivityItem[]>('/api/activity');
  if (!ok || !data) throw new Error(`fetchActivity failed (${status})`);
  return data;
}

export interface AttentionItem {
  id: string;
  leadId: string;
  leadName: string;
  text: string;
  action: string;
  ts: string;
}

export async function fetchAttentionItems(): Promise<AttentionItem[]> {
  const { data, ok, status } = await api.get<AttentionItem[]>('/api/escalations/attention');
  if (!ok || !data) throw new Error(`fetchAttentionItems failed (${status})`);
  return data;
}

export interface SequenceListItem {
  id: string;
  name: string;
  goal: 'transfer' | 'book' | 'close';
  active: boolean;
  trigger: string;
  enrolled: number;
  connectRate: number;
  bookedRate: number;
  exitDays: number;
  stepCount: number;
}

export async function fetchSequences(): Promise<SequenceListItem[]> {
  const { data, ok, status } = await api.get<SequenceListItem[]>('/api/sequences');
  if (!ok || !data) throw new Error(`fetchSequences failed (${status})`);
  return data;
}

// ── Thread event mutations ────────────────────────────────────────────────────

export type FeedbackValue = 'up' | 'down' | null;

export async function patchThreadEventFeedback(
  id: string,
  value: FeedbackValue,
): Promise<{ id: string; feedback: FeedbackValue }> {
  const { data, ok, status } = await api.patch<{ id: string; feedback: FeedbackValue }>(
    `/api/thread-events/${id}/feedback`,
    { value },
  );
  if (!ok || !data) throw new Error(`patchThreadEventFeedback(${id}) failed (${status})`);
  return data;
}
